import { Controller } from '@hotwired/stimulus'
import { badgeClasses } from '@/utils/badge_classes'

export default class extends Controller {
  static targets = ['emptyState', 'formContainer', 'addButton']

  connect() {
    this.hideEmptyStateIfNeeded()
  }

  showForm(event) {
    event.preventDefault()

    // Hide add button while form is shown
    if (this.hasAddButtonTarget) {
      this.addButtonTarget.classList.add('d-none')
    }

    // Create and show the form using nested-fields template
    const nestedFieldsController = this.application.getControllerForElementAndIdentifier(
      this.element,
      'nested-fields'
    )
    if (nestedFieldsController) {
      nestedFieldsController.addAssociation(event)
    }
  }

  save(event) {
    event.preventDefault()

    const formWrapper = event.target.closest('[data-automation-creator-target="formContainer"]')
    if (!formWrapper) return

    // Basic validation - check if at least one action is configured
    const actionSelects = formWrapper.querySelectorAll('select[name*="action_type"]')
    const hasActions = Array.from(actionSelects).some(select => {
      const wrapper = select.closest('.nested-fields')
      const destroyField = wrapper?.querySelector('input[name*="_destroy"]')
      return destroyField?.value !== '1'
    })

    if (!hasActions) {
      alert('Please add at least one action')
      return
    }

    // Convert form to summary view
    this.convertToSummary(formWrapper)

    // Show add button again
    if (this.hasAddButtonTarget) {
      this.addButtonTarget.classList.remove('d-none')
    }
  }

  cancel(event) {
    event.preventDefault()

    // Find the form - either by target or by traversing up
    let formWrapper = event.target.closest('[data-automation-creator-target="formContainer"]')

    // If not found by target, try finding by class (for edit mode)
    if (!formWrapper) {
      formWrapper = event.target.closest('.nested-fields:not(.d-none)')
    }

    if (formWrapper) {
      // Check if this form was previously saved (has summary inside it)
      const isSaved = formWrapper.dataset.automationSaved === 'true'
      const summaryInside = formWrapper.querySelector('.automation-summary-wrapper')

      if (isSaved || summaryInside) {
        // This form was already saved - hide the card and show the summary
        const cardElement = formWrapper.querySelector('.card')
        if (cardElement) {
          cardElement.classList.add('d-none')
        }
        if (summaryInside) {
          summaryInside.classList.remove('d-none')
        }
        formWrapper.removeAttribute('data-automation-creator-target')

        // Restore summary padding on the list-group-item
        const listGroupItem = formWrapper.closest('.list-group-item')
        if (listGroupItem) {
          listGroupItem.classList.remove('p-3')
          listGroupItem.classList.add('py-2', 'px-3')
        }
      } else {
        // New unsaved form - remove the entire list-group-item from DOM
        const listGroupItem = formWrapper.closest('.list-group-item')
        if (listGroupItem) {
          listGroupItem.remove()
        } else {
          formWrapper.remove()
        }
      }
    }

    // Show add button again
    if (this.hasAddButtonTarget) {
      this.addButtonTarget.classList.remove('d-none')
    }

    // Show empty state if no automations left
    this.hideEmptyStateIfNeeded()
  }

  convertToSummary(formWrapper) {
    // Get the form data to build summary
    const triggerSelect = formWrapper.querySelector('select[name*="[trigger_attributes][event_type]"]')
    const triggerType = triggerSelect?.value || 'status_changed'
    const triggerLabel = triggerSelect?.options[triggerSelect.selectedIndex]?.text || triggerType
    const triggerDetail = this.getTriggerDetail(formWrapper, triggerType)

    // Get actions HTML with details
    const actionsHtml = this.getActionsHtml(formWrapper)

    // Check if summary already exists (re-editing a JS-created automation)
    let summaryWrapper = formWrapper.querySelector('.automation-summary-wrapper')

    if (summaryWrapper) {
      // Update existing summary
      const summaryContent = summaryWrapper.querySelector('.d-flex > div:first-child')
      if (summaryContent) {
        summaryContent.innerHTML = `
          <div class="mb-1">
            <span class="${badgeClasses({ variant: 'primary', extra: 'me-1' })}">WHEN</span>
            <strong>${triggerLabel}</strong>
            <span class="text-muted">${triggerDetail}</span>
          </div>
          <div>
            <span class="${badgeClasses({ variant: 'success', extra: 'me-1' })}">THEN</span>
            ${actionsHtml}
          </div>
        `
      }
      summaryWrapper.classList.remove('d-none')
    } else {
      // Create summary wrapper (no list-group-item class - parent already has it)
      summaryWrapper = document.createElement('div')
      summaryWrapper.className = 'automation-summary-wrapper'
      summaryWrapper.innerHTML = `
        <div class="d-flex justify-content-between align-items-start">
          <div>
            <div class="mb-1">
              <span class="${badgeClasses({ variant: 'primary', extra: 'me-1' })}">WHEN</span>
              <strong>${triggerLabel}</strong>
              <span class="text-muted">${triggerDetail}</span>
            </div>
            <div>
              <span class="${badgeClasses({ variant: 'success', extra: 'me-1' })}">THEN</span>
              ${actionsHtml}
            </div>
          </div>
          <div class="d-flex align-items-center gap-2 ms-2">
            <button type="button" class="btn btn-sm btn-link text-primary p-0" data-action="click->automation-creator#edit" title="Edit">
              <i class="far fa-edit"></i>
            </button>
            <button type="button" class="btn btn-sm btn-link text-danger p-0" data-action="click->automation-creator#remove" title="Delete">
              <i class="far fa-trash-alt"></i>
            </button>
          </div>
        </div>
      `

      // Insert summary at the beginning of the form wrapper
      formWrapper.insertBefore(summaryWrapper, formWrapper.firstChild)
    }

    // Hide the card (the actual form) but keep the nested-fields visible
    const cardElement = formWrapper.querySelector('.card')
    if (cardElement) {
      cardElement.classList.add('d-none')
    }

    // Update the parent list-group-item padding (from p-3 for form to py-2 px-3 for summary)
    const listGroupItem = formWrapper.closest('.list-group-item')
    if (listGroupItem) {
      listGroupItem.classList.remove('p-3')
      listGroupItem.classList.add('py-2', 'px-3')
    }

    // Update data attributes
    formWrapper.removeAttribute('data-automation-creator-target')
    formWrapper.dataset.automationSaved = 'true'
  }

  getTriggerDetail(formWrapper, triggerType) {
    if (triggerType === 'status_changed') {
      const oldStatus = formWrapper.querySelector('select[name*="[old_status]"]')
      const newStatus = formWrapper.querySelector('select[name*="[new_status]"]')
      // These are multi-selects, get all selected options
      const oldSelected = oldStatus ? Array.from(oldStatus.selectedOptions).map(opt => opt.text) : []
      const newSelected = newStatus ? Array.from(newStatus.selectedOptions).map(opt => opt.text) : []
      const oldText = oldSelected.length > 0 ? oldSelected.join(', ') : 'Any'
      const newText = newSelected.length > 0 ? newSelected.join(', ') : 'Any'
      return ` (${oldText} → ${newText})`
    } else if (triggerType === 'field_changed') {
      const fieldConfig = formWrapper.querySelector('[data-automation-trigger-target="fieldConfig"]:not(.d-none)')
      const field = fieldConfig?.querySelector('select[name*="[field]"]')
      const fieldText = field?.options[field.selectedIndex]?.text || ''
      return fieldText ? ` (${fieldText})` : ''
    } else if (triggerType?.startsWith('date_')) {
      const amount = formWrapper.querySelector('input[name*="[amount]"]')?.value || '7'
      const unit = formWrapper.querySelector('select[name*="[unit]"]')
      const unitText = unit?.value || 'days'
      const direction = triggerType === 'date_after' ? 'after' : 'before'
      // Find the visible date field select (there are two, one for before and one for after)
      const dateFieldBefore = formWrapper.querySelector('[data-automation-trigger-target="dateFieldBefore"]:not(.d-none) select')
      const dateFieldAfter = formWrapper.querySelector('[data-automation-trigger-target="dateFieldAfter"]:not(.d-none) select')
      const dateField = dateFieldAfter || dateFieldBefore
      const dateFieldText = dateField?.options[dateField.selectedIndex]?.text || 'due date'
      return ` (${amount} ${unitText} ${direction} ${dateFieldText})`
    }
    return ''
  }

  getActionsHtml(formWrapper) {
    const actions = []
    formWrapper.querySelectorAll('.nested-fields').forEach(actionWrapper => {
      const destroyField = actionWrapper.querySelector('input[name*="_destroy"]')
      if (destroyField?.value === '1') return

      const actionSelect = actionWrapper.querySelector('select[name*="[action_type]"]')
      if (!actionSelect) return

      const actionType = actionSelect.value
      const actionLabel = actionSelect.options[actionSelect.selectedIndex]?.text || actionType

      let detail = ''
      if (actionType === 'send_notification') {
        const template = actionWrapper.querySelector('select[name*="config_notification_template"]')
        const templateText = template?.options[template.selectedIndex]?.text
        if (templateText && template.value) {
          detail += ` "${templateText}"`
        }

        const toSelect = actionWrapper.querySelector('select[name*="config_to"]')
        if (toSelect) {
          const selectedOptions = Array.from(toSelect.selectedOptions).map(opt => opt.text)
          if (selectedOptions.length > 0) {
            detail += ` to ${selectedOptions.join(', ')}`
          }
        }

        const deliveryMethods = []
        actionWrapper.querySelectorAll('input[name*="config_delivery_methods"]:checked').forEach(cb => {
          const label = cb.nextElementSibling?.textContent || cb.value
          deliveryMethods.push(label.trim())
        })
        if (deliveryMethods.length > 0) {
          detail += ` via ${deliveryMethods.join(', ')}`
        }
      } else if (actionType === 'send_reminder') {
        const toSelect = actionWrapper.querySelector('select[name*="config_to"]')
        if (toSelect) {
          const selectedOptions = Array.from(toSelect.selectedOptions).map(opt => opt.text)
          if (selectedOptions.length > 0) {
            detail += ` to ${selectedOptions.join(', ')}`
          }
        }

        const repeatType = actionWrapper.querySelector('select[name*="config_repeat_type"]')?.value
        if (repeatType === 'every') {
          const interval = actionWrapper.querySelector('input[name*="config_repeat_interval"]')?.value || '3'
          const unit = actionWrapper.querySelector('select[name*="config_repeat_unit"]')?.value || 'days'
          detail += `, every ${interval} ${unit}`
        }

        const stopStatus = actionWrapper.querySelector('select[name*="config_stop_status"]')
        const stopStatusValue = stopStatus?.value
        if (stopStatusValue) {
          const stopStatusText = stopStatus?.options[stopStatus.selectedIndex]?.text
          detail += ` until ${stopStatusText}`
        }
      }

      actions.push(`${actionLabel}${detail}`)
    })

    return actions.length > 0 ? actions.join('<br>') : '<span class="fst-italic text-muted">No actions</span>'
  }

  edit(event) {
    event.preventDefault()

    const summaryWrapper = event.target.closest('.automation-summary-wrapper')
    if (!summaryWrapper) return

    // Check if this is an existing automation (has edit form sibling) or new (card is inside parent)
    const editForm = summaryWrapper.nextElementSibling
    if (editForm && editForm.classList.contains('automation-edit-form')) {
      // Existing automation - show the edit form
      editForm.classList.remove('d-none')
      summaryWrapper.classList.add('d-none')
    } else {
      // New automation created via JS - the card is inside the same parent (nested-fields)
      const nestedFields = summaryWrapper.closest('.nested-fields')
      const cardElement = nestedFields?.querySelector('.card.d-none')
      if (cardElement) {
        cardElement.classList.remove('d-none')
        nestedFields.setAttribute('data-automation-creator-target', 'formContainer')
        summaryWrapper.classList.add('d-none')

        // Update the parent list-group-item padding (from py-2 px-3 for summary to p-3 for form)
        const listGroupItem = nestedFields.closest('.list-group-item')
        if (listGroupItem) {
          listGroupItem.classList.remove('py-2', 'px-3')
          listGroupItem.classList.add('p-3')
        }
      }
    }

    // Hide add button
    if (this.hasAddButtonTarget) {
      this.addButtonTarget.classList.add('d-none')
    }
  }

  cancelEdit(event) {
    event.preventDefault()

    const editForm = event.target.closest('.automation-edit-form')
    if (!editForm) return

    const summaryWrapper = editForm.previousElementSibling
    if (summaryWrapper && summaryWrapper.classList.contains('automation-summary-wrapper')) {
      // Hide form, show summary
      editForm.classList.add('d-none')
      summaryWrapper.classList.remove('d-none')
    }

    // Show add button
    if (this.hasAddButtonTarget) {
      this.addButtonTarget.classList.remove('d-none')
    }
  }

  saveEdit(event) {
    event.preventDefault()

    const editForm = event.target.closest('.automation-edit-form')
    if (!editForm) return

    // Basic validation
    const actionSelects = editForm.querySelectorAll('select[name*="action_type"]')
    const hasActions = Array.from(actionSelects).some(select => {
      const wrapper = select.closest('.nested-fields')
      const destroyField = wrapper?.querySelector('input[name*="_destroy"]')
      return destroyField?.value !== '1'
    })

    if (!hasActions) {
      alert('Please add at least one action')
      return
    }

    // Update summary with new values
    const summaryWrapper = editForm.previousElementSibling
    if (summaryWrapper && summaryWrapper.classList.contains('automation-summary-wrapper')) {
      this.updateSummaryFromForm(summaryWrapper, editForm)
      editForm.classList.add('d-none')
      summaryWrapper.classList.remove('d-none')
    }

    // Show add button
    if (this.hasAddButtonTarget) {
      this.addButtonTarget.classList.remove('d-none')
    }
  }

  updateSummaryFromForm(summaryWrapper, editForm) {
    // Find the content div (first child of the flex container)
    const summaryContent = summaryWrapper.querySelector('.d-flex > div:first-child')
    if (!summaryContent) return

    // Get trigger info from the edit form
    const triggerSelect = editForm.querySelector('select[name*="[trigger_attributes][event_type]"]')
    const triggerType = triggerSelect?.value || 'status_changed'
    const triggerLabel = triggerSelect?.options[triggerSelect.selectedIndex]?.text || triggerType
    const triggerDetail = this.getTriggerDetail(editForm, triggerType)

    // Get actions info
    const actionsHtml = this.getActionsHtml(editForm)

    summaryContent.innerHTML = `
      <div class="mb-1">
        <span class="${badgeClasses({ variant: 'primary', extra: 'me-1' })}">WHEN</span>
        <strong>${triggerLabel}</strong>
        <span class="text-muted">${triggerDetail}</span>
      </div>
      <div>
        <span class="${badgeClasses({ variant: 'success', extra: 'me-1' })}">THEN</span>
        ${actionsHtml}
      </div>
    `
  }

  remove(event) {
    event.preventDefault()

    const summaryWrapper = event.target.closest('.automation-summary-wrapper')
    if (!summaryWrapper) return

    // Find the parent nested-fields container
    const nestedFields = summaryWrapper.closest('.nested-fields')
    const listGroupItem = summaryWrapper.closest('.list-group-item')

    if (nestedFields) {
      // Check if this is a persisted record (has an id field with value) or new
      const idField = nestedFields.querySelector('input[name*="[id]"]')
      const isPersisted = idField && idField.value

      if (isPersisted) {
        // Existing record - mark for destruction and hide
        const destroyField = nestedFields.querySelector('input[name*="_destroy"]')
        if (destroyField) {
          destroyField.value = '1'
        }
        // Hide the list-group-item (or nested-fields if no list-group-item)
        if (listGroupItem) {
          listGroupItem.classList.add('d-none')
        } else {
          nestedFields.classList.add('d-none')
        }
      } else {
        // New record created via JS - remove from DOM entirely
        if (listGroupItem) {
          listGroupItem.remove()
        } else {
          nestedFields.remove()
        }
      }
    }

    // Show empty state if needed
    this.hideEmptyStateIfNeeded()
  }

  hideEmptyStateIfNeeded() {
    // Empty state is now handled server-side, no JS needed
  }
}
